import {
    tableMatrix,
    insertTableDialog,
    tableMatrixAriaLabel,
} from './InsertTablePane.locstring.json';
import { insertTable } from 'owa-locstrings/lib/strings/inserttable.locstring.json';
import loc, { format } from 'owa-localize';
import { observer } from 'mobx-react';
import * as React from 'react';

import { showModal } from 'owa-modal';
import { Editor } from 'roosterjs-editor-core';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';

import assign from 'lodash-es/assign';
import { InsertTableDialog } from './InsertTableDialog';

let styles = require('./InsertTablePane.scss');
let classNames = require('classnames/bind').bind(styles);

interface InsertTablePaneState {
    cols: number;
    rows: number;
}

interface InsertTablePaneProps {
    onInsertTable: (cols: number, rows: number) => void;
    editor: Editor;
    maxNumCols: number;
    maxNumRows: number;
}

/**
 * A callout that shows a grid of squares that may be clicked to designate the size of a table.
 */
@observer
export class InsertTablePane extends React.Component<InsertTablePaneProps, InsertTablePaneState> {
    private lastTarget: HTMLElement;
    private focusZoneRef: React.RefObject<FocusZone> = React.createRef();
    private cachedSelectionRange: Range;
    private ariaLabels: string[][];

    constructor(props: InsertTablePaneProps) {
        super(props);
        this.state = {
            cols: 0,
            rows: 0,
        };
    }

    static getDerivedStateFromProps(props: InsertTablePaneProps, state: InsertTablePaneState) {
        return assign(
            {},
            state.cols > props.maxNumCols ? { cols: Math.min(state.cols, props.maxNumCols) } : {},
            state.rows > props.maxNumRows ? { rows: Math.min(state.rows, props.maxNumRows) } : {}
        );
    }

    isCell(element: HTMLElement) {
        return element.classList.contains(styles.tableCell);
    }

    componentDidMount() {
        const { editor } = this.props;
        if (editor && !editor.isDisposed()) {
            this.cachedSelectionRange = editor.getSelectionRange();
        }

        // The focus zone ref is not guaranteed to be set until first mount/
        // If the lastTarget callback was made before the component's mount resolve
        // (e.g. if this component is in an owa-modal), update the selection
        // on mount.
        if (this.lastTarget) {
            this.focusZoneRef.current.focusElement(this.lastTarget);
        }
    }

    componentWillUnmount() {
        this.ariaLabels = null;
    }

    render() {
        let rowSet: JSX.Element[] = [];
        let { rows, cols } = this.state;
        this.ariaLabels = this.ariaLabels || this.createAriaLabels();

        for (let i = 1; i <= this.props.maxNumCols; i++) {
            let cells: JSX.Element[] = [];
            for (let j = 1; j <= this.props.maxNumRows; j++) {
                let className = classNames(styles.tableCell, {
                    hovered: i <= rows && j <= cols,
                });
                let key = `cell_${i}_${j}`;
                cells.push(
                    <div
                        className={className}
                        key={key}
                        id={key}
                        data-is-focusable={true}
                        // TODO #34957 Move to strings
                        aria-label={this.ariaLabels[i][j]}
                        aria-posinset={this.props.maxNumCols * i + j}
                    />
                );
            }
            rowSet.push(
                <div className={styles.tableRow} key={`row_${i}`}>
                    {cells}
                </div>
            );
        }
        let tableDescription =
            rows * cols > 0 ? format(loc(tableMatrix), cols, rows) : loc(insertTable);
        return (
            <div
                className={styles.tablePane}
                onMouseMove={this.onMouseMove}
                onMouseOut={this.onMouseOut}
                onClick={this.onCreateTable}
                onKeyDown={this.onKeyDown}>
                <div className={styles.tableTitle}>{tableDescription}</div>
                <FocusZone
                    ref={this.focusZoneRef}
                    defaultActiveElement="col_1_1"
                    elementType="div"
                    direction={FocusZoneDirection.bidirectional}
                    onActiveElementChanged={this.updateDimensionsToCell}>
                    {rowSet}
                </FocusZone>
                <div className={styles.customButton} onClick={this.showInsertTableDialog}>
                    {loc(insertTableDialog)}
                </div>
            </div>
        );
    }

    private onMouseMove = (e: React.MouseEvent<unknown>) => {
        this.updateDimensionsToCell(e.target);
    };

    private updateDimensionsToCell = (target: EventTarget) => {
        if (target instanceof HTMLElement && target != this.lastTarget && this.isCell(target)) {
            this.lastTarget = target;
            let id = target.id;
            let matches = id.match(/cell_(\w+)_(\w+)/);
            if (matches && matches.length == 3) {
                let row = parseInt(matches[1]);
                let col = parseInt(matches[2]);
                this.setState({
                    rows: row,
                    cols: col,
                });
            }
            if (this.focusZoneRef.current) {
                this.focusZoneRef.current.focusElement(target);
            }
        }
    };

    private onMouseOut = () => {
        this.setState({
            rows: 0,
            cols: 0,
        });
    };

    private onKeyDown = e => {
        if (e.key.toLowerCase() === 'enter') {
            this.onCreateTable();
            e.stopPropagation();
        }
    };

    private onCreateTable = () => {
        let { rows, cols } = this.state;
        if (rows * cols > 0 && this.props.onInsertTable) {
            this.props.onInsertTable(cols, rows);
        }
    };

    private showInsertTableDialog = async () => {
        const [showInsertTableDialogPromise] = showModal(InsertTableDialog);
        const value = await showInsertTableDialogPromise;
        if (!value) {
            // If the user dismissed the modal, do nothing
            return;
        }

        const cols = parseInt(value.colStr);
        const rows = parseInt(value.rowStr);
        const { editor, onInsertTable } = this.props;
        if (rows * cols > 0 && onInsertTable && editor && !editor.isDisposed()) {
            // Fix 43516: First set focus into editor, then change selection.
            // This is not a final fix. A final fix should be in roosterjs
            // But it seems need more time to investigate,
            // so let's make a workaround here for now to unblock the scenario.
            editor.focus();
            editor.select(this.cachedSelectionRange);
            onInsertTable(cols, rows);
        }
    };

    // It seems in Chrome the function _String._toFormattedString() (called by format()) has serious perf issue.
    // For each mouse move on the table picker, we need to calcuate the string 100 times for aria label.
    // Here let's just cache the result so that we don't need to recalcuate them when mouse move
    // The cache will be recalculated when the component is mounted again to align with current language
    private createAriaLabels(): string[][] {
        const result = [];
        for (let i = 1; i <= this.props.maxNumCols; i++) {
            let col: string[] = [];
            for (let j = 1; j <= this.props.maxNumRows; j++) {
                col[j] = format(loc(tableMatrixAriaLabel), j, i);
            }
            result[i] = col;
        }
        return result;
    }
}
